/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Login from './Login';
import AuthBrandPanel from 'components/common/AuthBrandPanel';
import ActionConfirmation from 'components/common/ActionConfirmation';
import Footer from 'components/common/Footer';
import PopUpSuccessScheme from '../common/PopUpScheme/PopUpSuccessScheme';
import { PopUpSessionEndedScheme } from 'components/common/PopUpScheme/PopUpSessionEndedScheme';
import { SESSION_STORAGE_CONSTANTS, SESSION_STORAGE_OPTIONS } from 'components/common/constants';
import { ROUTE_PATHS, INITIAL_PATH_AFTER_LOGIN } from 'routes/constants';
import { clearLocalStorage, setIsSuccessLoginStatus } from 'services/authUtils';
import i18nContext from 'components/i18n-context';
import { PopUp } from 'uikit/PopUp/PopUp';
import './Login.scss';

const LoginStages = ({ authStore }) => {
  const location = useLocation();
  const i18n = useContext(i18nContext);
  const [isSessionEnded, setIsSessionEnded] = useState(
    window.sessionStorage.getItem(SESSION_STORAGE_CONSTANTS.SESSION_ENDED)
  );

  // TRN-7473: the Registration page now links here in-app instead of with a full page reload, so this store can
  // still hold the error and the entered phone/credentials of a login attempt made before the user went over
  // there — a fresh page load always started without them. A lazy state initializer rather than an effect: it
  // has to run before <Login> below reads authStore.values.phone into its form's initial values on first render.
  useState(() => authStore.resetLoginForm());

  useEffect(() => {
    const handlePopState = () => {
      if (authStore.isLoginSuccess && location.pathname === ROUTE_PATHS.LOGIN) {
        authStore.reset();
        clearLocalStorage();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Ported from components/EntryLayouts/AuthorizationLayout.js — that layout is bypassed for this
  // route (see docs/specs/TRN-6921_email_login/spec.md §7.6), so this page owns the
  // post-2FA-success redirect itself.
  useEffect(() => {
    if (!authStore.isLoading && authStore.isLoginSuccess && authStore.isPinSuccess) {
      setIsSuccessLoginStatus(true);
      window.location.replace(INITIAL_PATH_AFTER_LOGIN);
    }
  }, [authStore.isLoading, authStore.isLoginSuccess, authStore.isPinSuccess]);

  const closeSessionEndedPopUp = () => {
    window.sessionStorage.removeItem(SESSION_STORAGE_CONSTANTS.SESSION_ENDED);
    setIsSessionEnded(false);
  };

  const handleSetPhone = (phone) => authStore.setPhone(phone);

  const handleSetUsername = (username) => authStore.setUsername(username);

  const handleSetPassword = (password) => authStore.setPassword(password);

  const handleSendAuthorizationData = async () => await authStore.sendAuthorizationData();

  return (
    <div className={'login-page'}>
      <AuthBrandPanel />
      <div className={'login-page__content'}>
        {/*
          TRN-7254 phase 3: the 2FA step is a modal overlay on top of the still-rendered login
          form now, not a full-page swap — Login stays mounted (its own Log In button just goes
          into its disabled/loading state for the whole 2FA-through-redirect stretch) so nothing
          but the ActionConfirmation modal disappears while typing the code, and there's no
          separate full-page loader to flash once isPinSuccess flips true — the button keeps
          spinning until window.location.replace above actually navigates away.
        */}
        <Login
          phone={authStore.values.phone}
          setPhone={handleSetPhone}
          setUsername={handleSetUsername}
          setPassword={handleSetPassword}
          sendAuthorizationData={handleSendAuthorizationData}
          error={authStore.error}
          isLoading={authStore.isLoading || authStore.isLoginSuccess}
        />
        {authStore.isLoginSuccess && <ActionConfirmation />}
        <div className={'login-page__footer'}>
          <Footer />
        </div>
      </div>
      <PopUp show={isSessionEnded} onClose={() => closeSessionEndedPopUp()}>
        {isSessionEnded === SESSION_STORAGE_OPTIONS.SESSION_ENDED ? (
          <PopUpSessionEndedScheme onClose={() => closeSessionEndedPopUp()} />
        ) : (
          <PopUpSuccessScheme
            onClose={() => closeSessionEndedPopUp()}
            message={i18n.getMessage('popUp.success.passwordRecovery')}
          />
        )}
      </PopUp>
    </div>
  );
};

LoginStages.propTypes = {
  authStore: MobXPropTypes.observableObject
};

export default inject('authStore')(observer(LoginStages));
